import { useEffect, useState } from 'react';
import styles from './SignupForm.module.scss';
import { Button, Heading, Paragraph, Form, FormfieldSelect, FormfieldString, Spinner } from 'ui/components';
import { useForm, SubmitHandler, useFormState } from 'react-hook-form';
import countryList from 'react-select-country-list';
import classNames from 'classnames';
import logger from 'helpers/logger';

interface FormData {
	email: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	faxNumber: number;
	company: string;
	address: string;
	addressLine2: string;
	postalCode: string;
	city: string;
	state: string;
	country: string;
	shopRole: string;
	invoiceEmail: string;
	userCaptcha: string;
	ReCaptchaToken?: string;
}

export interface SignupFormProps {
	contactTitle?: string;
	contactDescription?: string;
	shippingTitle?: string;
	shippingDescription?: string;
	shopRoleTitle?: string;
	shopRoleDescription?: string;
	invoiceTitle?: string;
	invoiceDescription?: string;
	onFormSubmit: (data: FormData) => void;
	dictionary?: { [key: string]: string };
	isLoading?: boolean;
	formIsSent?: boolean;
	reCaptchaApiKey?: string | undefined;
	userBrowserLocale?: string;
}

declare global {
	interface Window {
		grecaptcha: any;
	}
}

export const SignupForm: React.FC<SignupFormProps> = ({
	contactTitle,
	contactDescription,
	shippingTitle,
	shippingDescription,
	shopRoleTitle,
	shopRoleDescription,
	invoiceTitle,
	invoiceDescription,
	onFormSubmit,
	dictionary,
	isLoading,
	formIsSent,
	reCaptchaApiKey,
	userBrowserLocale,
}) => {
	// FORM HANDLING
	const [allErrors, setallErrors] = useState(0);
	const [errorSummary, setErrorSummary] = useState(0);
	const { register, handleSubmit, control, getValues, setError } = useForm<FormData>({
		mode: 'onBlur',
		defaultValues: {
			country: '',
			shopRole: '',
		},
	});

	/* eslint-disable @typescript-eslint/no-var-requires */
	const reactSimpleCaptcha = require('react-simple-captcha');
	const { LoadCanvasTemplate } = reactSimpleCaptcha;
	const { loadCaptchaEnginge } = reactSimpleCaptcha;
	const { validateCaptcha } = reactSimpleCaptcha;

	window.grecaptcha = window.grecaptcha || {};
	const [scriptLoaded, setScriptLoaded] = useState(false);
	const [isGeneratingToken, setIsGeneratingToken] = useState(false);
	const [captchaError, setCaptchaError] = useState<string | null>(null);

	// Helper function to generate fresh token
	const generateReCaptchaToken = (): Promise<string> => {
		return new Promise((resolve, reject) => {
			if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
				window.grecaptcha.ready(() => {
					window.grecaptcha
						.execute(reCaptchaApiKey)
						.then((token: string) => {
							resolve(token);
						})
						.catch((error: any) => {
							reject(error);
						});
				});
			} else {
				reject(new Error('grecaptcha not available'));
			}
		});
	};

	// Load captcha script when form is visible
	useEffect(() => {
		if (formIsSent || scriptLoaded) return;

		// Chinese users get simple canvas captcha
		if (userBrowserLocale === 'zh') {
			document.getElementById('reload_href').textContent = dictionary.formCaptchaReload;
			const canvElement = document.getElementById('canv');
			if (canvElement) canvElement.style.border = '1px solid black';
			loadCaptchaEnginge(6);
			setScriptLoaded(true);
			return;
		}

		// Check if reCAPTCHA script already exists
		const existingScript = document.querySelector(`script[src*="recaptcha/api.js"]`);
		if (existingScript) {
			setScriptLoaded(true);
			return;
		}

		// Load reCAPTCHA script dynamically
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/api.js?render=${reCaptchaApiKey}`;
		script.addEventListener('load', () => {
			setScriptLoaded(true);
		});
		script.addEventListener('error', () => {
			logger.error('Failed to load reCAPTCHA script', { siteKey: reCaptchaApiKey });
			setCaptchaError(dictionary.formCaptchaTokenError);
		});
		document.body.appendChild(script);
	}, [formIsSent, scriptLoaded, userBrowserLocale, reCaptchaApiKey, dictionary]);

	const onSubmit: SubmitHandler<FormData> = async (data) => {
		setCaptchaError(null);

		// Handle Chinese canvas captcha validation
		if (userBrowserLocale === 'zh') {
			if (!validateCaptcha(getValues('userCaptcha'))) {
				setCaptchaError(dictionary.formCaptchaInvalid);
				loadCaptchaEnginge(6);
				return;
			}
			onFormSubmit(data);
			return;
		}

		// Handle reCAPTCHA validation
		if (!scriptLoaded) {
			logger.error('reCAPTCHA script not loaded on form submit');
			setCaptchaError(dictionary.formErrorMessage);
			return;
		}

		try {
			setIsGeneratingToken(true);
			const freshToken = await generateReCaptchaToken();

			onFormSubmit({ ...data, ReCaptchaToken: freshToken });
		} catch (error) {
			logger.error('Failed to generate reCAPTCHA token', error);
			setCaptchaError(dictionary.formCaptchaTokenError);
		} finally {
			setIsGeneratingToken(false);
		}
	};

	const { errors } = useFormState({
		control,
	});

	const onError = (data) => {
		const errs = { ...data };
		const { country, shopRole } = getValues();

		// Manually set error for select fields if they are empty or still have the placeholder value
		if (country === '' || country === dictionary?.formCountrySelect) {
			errs.country = errs.country ?? { type: 'manual', message: 'Please choose a valid country' };
			setError('country', errs.country);
		}
		if (shopRole === '' || shopRole === dictionary?.formShopRoleSelect) {
			errs.shopRole = errs.shopRole ?? { type: 'manual', message: 'Please choose a valid role' };
			setError('shopRole', errs.shopRole);
		}

		setErrorSummary(Object.keys(data).length);
		setallErrors(errs);
	};

	// Populate country selector with all countries
	// Map library code → CMS code
	const ISO_TO_CMS: Record<string, string> = { GB: 'UK' };
	const raw = countryList().getData(); // with UK data: [{ label: 'United Kingdom', value: 'GB' }
	const countrySelectOptions = [
		{
			label: '',
			value: '',
			text: dictionary?.formCountrySelect || '',
			hidden: false,
			disabled: true,
		}, // placeholder
		...raw.map(({ label, value }) => {
			const mappedValue = ISO_TO_CMS[value] ?? value; // GB → UK, others unchanged
			return {
				label, // keep label for compatibility if you use it elsewhere
				value: mappedValue, // <- swapped to UK
				text: label, // visual text
				hidden: false,
				disabled: false,
			};
		}),
	];

	// NO NUMBERS REGEX
	const regexNoNumbers = new RegExp(/^([^0-9]*)$/);
	const textOnlyInputRules = {
		pattern: {
			value: regexNoNumbers,
			message: 'Numbers are not allowed here',
		},
	};
	const textOnlyInputKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const regex = regexNoNumbers;

		// preventDefault if the key pressed is a number
		if (!regex.test(event.key)) {
			event.preventDefault();
		}
	};

	// NO LETTERS REGEX
	const regexNoLetters = new RegExp(/^\d+$/);
	const numbersOnlyInputRules = {
		pattern: {
			value: regexNoLetters,
			message: 'Letters are not allowed here',
		},
	};
	const numbersOnlyInputKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const regex = regexNoLetters;

		if (!regex.test(event.key)) {
			event.preventDefault();
		}
	};

	// VALID PHONE NUMBER REGEX
	const regexValidPhoneNumber = new RegExp(/^[\d()\-+]+$/);
	const validPhoneNumberInputRules = {
		pattern: {
			value: regexValidPhoneNumber,
			message: 'Phone number is invalid',
		},
	};
	const validPhoneNumberInputKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
		const regex = regexValidPhoneNumber;
		if (!regex.test(event.key)) {
			event.preventDefault();
		}
	};
	// / FORM HANDLING

	return (
		<>
			{formIsSent ? (
				<div className={styles.SignupForm_success}>
					<Heading headingLevel="h2">{dictionary?.formThankYou}</Heading>
				</div>
			) : (
				<Form className={styles.ModalSignupForm} onSubmit={handleSubmit(onSubmit, onError)} errorSummary={errorSummary}>
					{/* Contact section */}
					{contactTitle && <Heading headingLevel="h2">{contactTitle}</Heading>}
					{contactDescription && (
						<Paragraph className="u-bottom-margin--md">
							<span
								dangerouslySetInnerHTML={{
									__html: contactDescription,
								}}
							></span>
						</Paragraph>
					)}

					<FormfieldString
						id="email"
						name="email"
						label={dictionary?.formEmail}
						type="email"
						register={register}
						className={classNames(styles.Formfield, 'u-top-margin--md', 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.email
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.email?.message || dictionary?.formErrorMessage}
					/>

					<FormfieldString
						id="firstName"
						name="firstName"
						label={dictionary?.formFirstName}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.firstName
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.firstName?.message}
						rules={textOnlyInputRules}
						handleKeyPress={textOnlyInputKeyPressHandler}
					/>

					<FormfieldString
						id="lastName"
						name="lastName"
						label={dictionary?.formLastName}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.lastName
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.lastName?.message}
						rules={textOnlyInputRules}
						handleKeyPress={textOnlyInputKeyPressHandler}
					/>

					<FormfieldString
						id="phoneNumber"
						name="phoneNumber"
						label={dictionary?.formPhoneNumber}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.phoneNumber
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.phoneNumber?.message}
						rules={validPhoneNumberInputRules}
						handleKeyPress={validPhoneNumberInputKeyPressHandler}
					/>

					<FormfieldString
						id="faxNumber"
						name="faxNumber"
						label={dictionary?.formFaxNumber}
						type="number"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.faxNumber
								? {
										hasError: true,
										required: false,
								  }
								: { required: false }
						}
						errorMessage={errors.faxNumber?.message}
						rules={numbersOnlyInputRules}
						handleKeyPress={numbersOnlyInputKeyPressHandler}
					/>

					{/* Shipping section */}
					{shippingTitle && (
						<Heading className={classNames('u-top-margin--xl u-bottom-margin--md')} headingLevel="h2">
							{shippingTitle}
						</Heading>
					)}
					{shippingDescription && <Paragraph className="u-bottom-margin--md">{shippingDescription}</Paragraph>}

					<FormfieldString
						id="company"
						name="company"
						label={dictionary?.formCompanyName}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-top-margin--md u-bottom-margin--md')}
						layout="row"
						state={
							errors.company
								? {
										hasError: true,
										required: true,
										hasTextNoWrap: true,
								  }
								: { required: true, hasTextNoWrap: true }
						}
						errorMessage={errors.company?.message}
					/>

					<FormfieldString
						id="address"
						name="address"
						label={dictionary?.formAddress}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.address
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.address?.message}
					/>

					<FormfieldString
						id="addressLine2"
						name="addressLine2"
						label={dictionary?.formAddressLine2}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.addressLine2
								? {
										hasError: true,
										required: false,
								  }
								: { required: false }
						}
						errorMessage={errors.addressLine2?.message}
					/>

					<FormfieldString
						id="city"
						name="city"
						label={dictionary?.formCity}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.city
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.city?.message}
					/>

					<FormfieldString
						id="postalCode"
						name="postalCode"
						label={dictionary?.formPostalCode}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.postalCode
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.postalCode?.message}
					/>

					<FormfieldString
						id="state"
						name="state"
						label={dictionary?.formState}
						type="text"
						register={register}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.state
								? {
										hasError: true,
										required: false,
								  }
								: { required: false }
						}
						errorMessage={errors.state?.message}
					/>

					<FormfieldSelect
						id="country"
						name="country"
						label={dictionary?.formCountry}
						options={countrySelectOptions}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						register={register}
						state={
							errors.country
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.country?.message}
					/>

					{/* Shop Role section */}
					{shopRoleTitle && (
						<Heading className={classNames('u-top-margin--xl')} headingLevel="h2">
							{shopRoleTitle}
						</Heading>
					)}
					{shopRoleDescription && <Paragraph className="u-bottom-margin--md">{shopRoleDescription}</Paragraph>}

					<FormfieldSelect
						id="shopRole"
						name="shopRole"
						label={dictionary?.formShopRole}
						options={[
							{
								value: '',
								text: dictionary?.formShopRoleSelect || '',
								hidden: false,
								disabled: true,
							},
							{
								value: 'businessPartner',
								text: dictionary?.formShopRoleBusinessPartner,
							},
							{
								value: 'reseller',
								text: dictionary?.formShopRoleReseller,
							},
						]}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						register={register}
						state={
							errors.shopRole
								? {
										hasError: true,
										required: true,
								  }
								: { required: true }
						}
						errorMessage={errors.shopRole?.message || dictionary?.formErrorMessage}
					/>

					{/* Invoicing section */}
					{invoiceTitle && (
						<Heading className={classNames('u-top-margin--xl')} headingLevel="h2">
							{invoiceTitle}
						</Heading>
					)}
					{invoiceDescription && <Paragraph className="u-bottom-margin--md">{invoiceDescription}</Paragraph>}

					<FormfieldString
						id="invoiceEmail"
						name="invoiceEmail"
						label={dictionary?.formInvoiceEmailAddress}
						type="email"
						errorMessage={errors.invoiceEmail?.message || dictionary?.formErrorMessage}
						className={classNames(styles.Formfield, 'u-bottom-margin--md')}
						layout="row"
						state={
							errors.invoiceEmail
								? {
										hasError: true,
										required: false,
								  }
								: { required: false }
						}
						register={register}
					/>

					{/* Captcha */}
					{userBrowserLocale === 'zh' ? (
						<>
							<LoadCanvasTemplate />
							<FormfieldString
								id="userCaptcha"
								name="userCaptcha"
								type="text"
								state={
									errors.userCaptcha
										? {
												hasError: true,
												required: true,
										  }
										: { required: true }
								}
								register={register}
								label={dictionary?.formCaptcha}
								layout="row"
							/>
						</>
					) : (
						<div className="g-recaptcha" data-sitekey={reCaptchaApiKey} data-size="invisible" />
					)}

					{captchaError && (
						<Paragraph className={classNames(styles.SignupForm_formErrorMessage, 'u-top-margin--md', 'u-bottom-margin--md')}>
							{captchaError}
						</Paragraph>
					)}

					{/* Submit */}
					<div className={classNames(styles.SignupForm_buttonWrapper, 'u-top-margin--xl')}>
						{isLoading || isGeneratingToken ? (
							<div>
								<Spinner />
							</div>
						) : (
							<Button style="primary" type="submit" aria-label={dictionary?.formSend}>
								{dictionary?.formSend}
							</Button>
						)}
					</div>
				</Form>
			)}
		</>
	);
};
